import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./Maps.css";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

const Maps = () => {
  const [maps, setMaps] = useState([]);
  const [newMap, setNewMap] = useState({
    siteName: "",
    location: "",
    notes: "",
    fileUpload: [],
  });
  const [propertiesList, setPropertiesList] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedMap, setEditedMap] = useState({});
  const [showAddMapPopup, setShowAddMapPopup] = useState(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const storage = getStorage();

  useEffect(() => {
    const fetchMaps = async () => {
      try {
        const mapsRef = collection(db, "maps");
        const querySnapshot = await getDocs(mapsRef);
        const mapsList = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMaps(mapsList);
      } catch (error) {
        console.error("Error fetching maps:", error);
      }
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, "properties");
        const querySnapshot = await getDocs(propertiesRef);
        const properties = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setPropertiesList(properties);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchMaps();
    fetchProperties();
  }, []);

  const sortedPropertiesList = useMemo(() => {
    return [...propertiesList].sort((a, b) =>
      (a.propertyName || "").localeCompare(b.propertyName || "")
    );
  }, [propertiesList]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMap((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChangeForEdit = (e) => {
    const { name, value } = e.target;
    setEditedMap((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!newMap.siteName) {
      alert("Please select a Site Name before uploading files.");
      return;
    }

    if (!files.length) return;

    try {
      setIsUploading(true);

      const uploadedFiles = [...(newMap.fileUpload || [])];

      for (const file of files) {
        const safeFileName = `${Date.now()}-${file.name}`;
        const storageRef = ref(storage, `maps/${newMap.siteName}/${safeFileName}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedFiles.push(downloadURL);
      }

      setNewMap((prev) => ({
        ...prev,
        fileUpload: uploadedFiles,
      }));
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("File upload failed. Check the console for details.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUploadForEdit = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!editedMap.siteName) {
      alert("Please enter a Site Name before uploading files.");
      return;
    }

    if (!files.length) return;

    try {
      setIsUploading(true);

      const uploadedFiles = [...(editedMap.fileUpload || [])];

      for (const file of files) {
        const safeFileName = `${Date.now()}-${file.name}`;
        const storageRef = ref(storage, `maps/${editedMap.siteName}/${safeFileName}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedFiles.push(downloadURL);
      }

      setEditedMap((prev) => ({
        ...prev,
        fileUpload: uploadedFiles,
      }));
    } catch (error) {
      console.error("Error uploading files in edit mode:", error);
      alert("File upload failed. Check the console for details.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async (index) => {
    const fileURL = editedMap.fileUpload?.[index];
    if (!fileURL) return;

    try {
      const fileRef = ref(storage, fileURL);
      await deleteObject(fileRef);

      const updatedFiles = editedMap.fileUpload.filter((_, i) => i !== index);
      setEditedMap((prev) => ({ ...prev, fileUpload: updatedFiles }));
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Could not delete file.");
    }
  };

  const handleAddMap = async () => {
    const mandatoryFields = ["siteName", "location", "notes"];

    for (const field of mandatoryFields) {
      if (!newMap[field]?.trim()) {
        alert(`Please fill out the ${field} field.`);
        return;
      }
    }

    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }

    try {
      const mapsRef = collection(db, "maps");
      const payload = {
        ...newMap,
        fileUpload: newMap.fileUpload || [],
      };

      const docRef = await addDoc(mapsRef, payload);

      setMaps((prev) => [...prev, { id: docRef.id, ...payload }]);
      setNewMap({
        siteName: "",
        location: "",
        notes: "",
        fileUpload: [],
      });
      setShowAddMapPopup(false);
    } catch (error) {
      console.error("Error adding map:", error);
      alert("Error saving map.");
    }
  };

  const handleSaveChanges = async () => {
    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }

    try {
      const mapDoc = doc(db, "maps", editedMap.id);

      const payload = {
        siteName: editedMap.siteName || "",
        location: editedMap.location || "",
        notes: editedMap.notes || "",
        fileUpload: editedMap.fileUpload || [],
      };

      await updateDoc(mapDoc, payload);

      setMaps((prevMaps) =>
        prevMaps.map((map) =>
          map.id === editedMap.id ? { ...map, ...payload } : map
        )
      );

      setSelectedMap((prev) => (prev ? { ...prev, ...payload } : prev));
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving map changes:", error);
      alert("Error saving changes.");
    }
  };

  const handleDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      const mapDoc = doc(db, "maps", selectedMap.id);
      await deleteDoc(mapDoc);

      setMaps((prev) => prev.filter((map) => map.id !== selectedMap.id));
      setShowDetailsPopup(false);
      setSelectedMap(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting map:", error);
      alert("Error deleting map.");
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDetailsClick = (map) => {
    setSelectedMap(map);
    setEditedMap({
      ...map,
      fileUpload: map.fileUpload || [],
    });
    setShowDetailsPopup(true);
    setIsEditing(false);
  };

  const closeModal = () => {
    setShowAddMapPopup(false);
    setShowDetailsPopup(false);
    setShowDeleteConfirmation(false);
    setSelectedMap(null);
    setIsEditing(false);
    setNewMap({
      siteName: "",
      location: "",
      notes: "",
      fileUpload: [],
    });
  };

  return (
    <div className="maps-page">
      <h1>Property Maps</h1>
      <button className="blue-button" onClick={() => setShowAddMapPopup(true)}>
        Click Here to Add Maps
      </button>

      {showAddMapPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Maps</h2>
            <div className="form-grid">
              <div>
                <label>
                  Site Name <span className="required">*</span>
                </label>
                <select
                  name="siteName"
                  onChange={handleInputChange}
                  value={newMap.siteName}
                  required
                >
                  <option value="">Select Site Name</option>
                  {sortedPropertiesList.map((property) => (
                    <option key={property.id} value={property.propertyName}>
                      {property.propertyName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label>
                  Location <span className="required">*</span>
                </label>
                <input
                  name="location"
                  onChange={handleInputChange}
                  value={newMap.location}
                />
              </div>

              <div>
                <label>
                  Notes <span className="required">*</span>
                </label>
                <input
                  name="notes"
                  onChange={handleInputChange}
                  value={newMap.notes}
                />
              </div>

              <div>
                <label>File Upload</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpeg,.jpg,.heic,.png,.xlsx"
                  onChange={handleFileUpload}
                />
                {Array.isArray(newMap.fileUpload) &&
                  newMap.fileUpload.map((file, index) => (
                    <div key={index} className="file-item">
                      <a href={file} target="_blank" rel="noopener noreferrer">
                        File {index + 1}
                      </a>
                    </div>
                  ))}
              </div>
            </div>

            <div className="modal-buttons">
              <button
                className="blue-button"
                onClick={handleAddMap}
                disabled={isUploading}
              >
                {isUploading ? "Uploading..." : "Save"}
              </button>
              <button className="red-button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Map Details</h2>
            <div className="popup-content">
              {isEditing ? (
                <div className="form-grid">
                  <div>
                    <label>Site Name</label>
                    <input
                      name="siteName"
                      value={editedMap.siteName || ""}
                      onChange={handleInputChangeForEdit}
                    />
                  </div>

                  <div>
                    <label>Location</label>
                    <input
                      name="location"
                      value={editedMap.location || ""}
                      onChange={handleInputChangeForEdit}
                    />
                  </div>

                  <div>
                    <label>Notes</label>
                    <input
                      name="notes"
                      value={editedMap.notes || ""}
                      onChange={handleInputChangeForEdit}
                    />
                  </div>

                  <div>
                    <label>File Upload</label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpeg,.jpg,.heic,.png,.xlsx"
                      onChange={handleFileUploadForEdit}
                    />
                    {(editedMap.fileUpload || []).map((file, index) => (
                      <div key={index} className="file-item">
                        <a href={file} target="_blank" rel="noopener noreferrer">
                          File {index + 1}
                        </a>
                        <button
                          type="button"
                          className="delete-button"
                          onClick={() => handleDeleteImage(index)}
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="modal-buttons">
                    <button
                      className="blue-button"
                      onClick={handleSaveChanges}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Save"}
                    </button>
                    <button
                      className="red-button"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p>
                    <strong>Site Name:</strong> {selectedMap?.siteName}
                  </p>
                  <p>
                    <strong>Location:</strong> {selectedMap?.location}
                  </p>
                  <p>
                    <strong>Notes:</strong> {selectedMap?.notes}
                  </p>
                  <p>
                    <strong>Files:</strong>
                  </p>

                  {Array.isArray(selectedMap?.fileUpload) &&
                    selectedMap.fileUpload.map((file, index) => (
                      <div key={index}>
                        <a href={file} target="_blank" rel="noopener noreferrer">
                          File {index + 1}
                        </a>
                      </div>
                    ))}

                  <div className="modal-buttons">
                    <button
                      className="blue-button"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </button>
                    <button
                      className="red-button"
                      onClick={handleDeleteConfirmation}
                    >
                      Delete
                    </button>
                    <button className="green-button" onClick={closeModal}>
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Are you sure you want to delete?</h2>
            <div className="modal-buttons">
              <button className="red-button" onClick={confirmDelete}>
                Yes
              </button>
              <button className="blue-button" onClick={cancelDelete}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <h2>Property Maps List</h2>
      <table className="maps-table">
        <thead>
          <tr>
            <th>Site Name</th>
            <th>Location</th>
            <th>Details</th>
            <th>Files</th>
          </tr>
        </thead>
        <tbody>
          {maps.map((map) => (
            <tr key={map.id}>
              <td>{map.siteName}</td>
              <td>{map.location}</td>
              <td>
                <button
                  className="blue-button"
                  onClick={() => handleDetailsClick(map)}
                >
                  Details
                </button>
              </td>
              <td>
                {Array.isArray(map.fileUpload) &&
                  map.fileUpload.map((file, index) => (
                    <button
                      key={index}
                      className="blue-button"
                      onClick={() => window.open(file, "_blank")}
                    >
                      File {index + 1}
                    </button>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Maps;